import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/axios';
import authManager from '../utils/authManager';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // 兼容 base64url 且保留 UTF-8 字符的 JWT 解析
  const decodeJwtPayloadUtf8 = (token) => {
    try {
      const part = token.split('.')[1];
      if (!part) return null;
      const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
      const pad = '='.repeat((4 - (b64.length % 4)) % 4);
      const base64 = b64 + pad;
      const binary = atob(base64);
      // 优先使用 TextDecoder
      try {
        const bytes = Uint8Array.from(binary, ch => ch.charCodeAt(0));
        const jsonStr = new TextDecoder('utf-8').decode(bytes);
        return JSON.parse(jsonStr);
      } catch (_) {
        // 退回 escape/encode 方案
        const jsonStr = decodeURIComponent(escape(binary));
        return JSON.parse(jsonStr);
      }
    } catch (_) {
      return null;
    }
  };

  // 检查认证状态
  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      // 先检查token是否有效
      if (!authManager.isTokenValid()) {
        console.warn('Token已过期，正在跳转到登录页面...');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        setUser(null);
        setPermissions([]);
        setLoading(false);
        // 跳转到登录页面
        if (typeof window !== 'undefined' && window.location?.pathname !== '/mobile/call') {
          window.location.href = '/';
        }
        return;
      }
      
      try {
        // 先从localStorage获取用户信息，提高登录速度
        const storedUser = localStorage.getItem('user');
        const storedPermissions = localStorage.getItem('permissions');
        
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        } else {
          // 从token中解析用户信息
          const userInfo = decodeJwtPayloadUtf8(token);
          setUser(userInfo);
        }
        
        if (storedPermissions) {
          setPermissions(JSON.parse(storedPermissions));
        } else {
          // 获取用户权限
          try {
            const response = await api.get('/users/permissions/me');
            setPermissions(response.data.permissions);
            localStorage.setItem('permissions', JSON.stringify(response.data.permissions));
          } catch (permissionsError) {
            console.error('获取权限失败:', permissionsError);
            // 权限获取失败不影响登录，设置空权限
            setPermissions([]);
            localStorage.setItem('permissions', JSON.stringify([]));
          }
        }
      } catch (error) {
        console.error('Token验证失败:', error);
        // 只有当token确实无效时才清除认证信息
        if (!authManager.isTokenValid()) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('permissions');
          setUser(null);
          setPermissions([]);
          // 跳转到登录页面
          if (typeof window !== 'undefined' && window.location?.pathname !== '/mobile/call') {
            window.location.href = '/';
          }
        }
      }
    }
    setLoading(false);
  };

  // 登录
  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login', {
        username,
        password
      });

      const { token, user: userData } = response.data;
      
      // 只有在登录成功时才设置用户状态和token
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      // 获取用户权限
      try {
        const permissionsResponse = await api.get('/users/permissions/me');
        setPermissions(permissionsResponse.data.permissions);
        localStorage.setItem('permissions', JSON.stringify(permissionsResponse.data.permissions));
      } catch (permissionsError) {
        console.error('获取权限失败:', permissionsError);
        // 权限获取失败不影响登录，设置空权限
        setPermissions([]);
        localStorage.setItem('permissions', JSON.stringify([]));
      }

      return { success: true };
    } catch (error) {
      console.error('登录失败:', error);
      // 确保登录失败时清除任何可能的状态
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('permissions');
      setUser(null);
      setPermissions([]);
      return { 
        success: false, 
        message: error.response?.data?.message || '用户名或密码错误' 
      };
    }
  };

  // 登出
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPermissions([]);
  };

  // 强制登出（用于token过期等情况）
  const forceLogout = () => {
    logout();
    // 跳转到登录页面
    if (typeof window !== 'undefined' && window.location?.pathname !== '/mobile/call') {
      window.location.href = '/';
    }
  };

  // 检查权限
  const hasPermission = (permission) => {
    if (!user || user.role === 'super_admin') {
      return true;
    }
    return permissions.includes(permission);
  };

  useEffect(() => {
    checkAuth();
    
    // 设置authManager的强制登出回调
    authManager.setForceLogoutCallback(() => {
      setUser(null);
      setPermissions([]);
      // 跳转到登录页面
      if (typeof window !== 'undefined' && window.location?.pathname !== '/mobile/call') {
        window.location.href = '/';
      }
    });
    
    // 启动token刷新监控
    authManager.startTokenRefreshMonitor();
    
    // 清理函数
    return () => {
      authManager.clearTokenRefreshTimer();
    };
  }, []);

  const value = {
    user,
    permissions,
    loading,
    login,
    logout,
    forceLogout,
    hasPermission,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 